import type { AxiosError, AxiosRequestConfig, ResponseType } from 'axios'

import axios from 'axios'

import { setServerVersion } from '../store/modules'

import { getIdToken } from './cognito.js'
import { getStore } from './state.js'

interface Options {
  clientName?: string | boolean
  absolute?: boolean
  signal?: AbortSignal
  validateStatus?: ((status: number) => boolean) | null
  auth?: string | false
  responseType?: ResponseType
}

export function isAbortErr (err: AxiosError): err is AxiosError {
  return (
  // Axios abort error implementation
    err?.code === 'ERR_CANCELED' ||

        // Fetch abort error implementation
        err?.name === 'AbortError'
  )
}

let axiosInstance = axios.create({
  withCredentials: true
})

let handle = axiosInstance.interceptors.response.use(response => {
  let ref = response.headers['x-build-commit-ref']?.trim() ?? undefined
  let date = response.headers['x-build-commit-date']?.trim() ?? undefined

  if (ref ?? date) {
    let store = getStore()
    store.dispatch(setServerVersion({ ref, date }))
  }

  return response
})

void handle

export const BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/api'
  : '/api'

async function request (method: string, endpoint: string, data: unknown, opts: Options = {}): Promise<unknown> {
  let state = getStore().getState()

  let clientName = opts.clientName === false
    ? undefined
    : opts.clientName ??
        state.patient?.clientName ??
        state.globalVars?.params?.clientName ??
        undefined

  let options: AxiosRequestConfig = {
    headers: {
      'Accept': 'application/json',
      ...(data instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      'X-Client-Name': clientName
    }
  }

  if (options?.headers?.['X-Client-Name'] === undefined) {
    delete options?.headers?.['X-Client-Name']
  }

  options.url = `${opts.absolute ? '' : BASE_URL}${endpoint}`
  options.method = method
  options.signal = opts.signal
  options.validateStatus = opts.validateStatus
  options.responseType = opts.responseType

  if (method === 'get') {
    options.params = data
  } else if (endpoint === '/documents') {
    options.data = data
  } else {
    options.data = JSON.stringify(data)
  }

  if (opts?.auth === false) {
    return await axiosInstance(options)
      .then(res => res.data)
  }
  if (opts.auth && options.headers) {
    options.headers.Authorization = `Bearer ${opts?.auth}`
    return await axiosInstance(options)
  }

  return getIdToken()
    .then(async (idToken: string) => {
      if (options.headers) {
        options.headers.Authorization = `Bearer ${idToken}`
        return await axiosInstance(options)
      }
    })
  // @ts-expect-error // it's okay
    .then(res => res.data)
}

async function getRequest<T> (endpoint: string, data?: unknown, options = {}): Promise<T extends object ? T : any> {
  return await (request('get', endpoint, data, options) as Promise<T extends object ? T : any>)
}

async function postRequest<T> (endpoint: string, data?: unknown, options = {}): Promise<T extends object ? T : any> {
  return await (request('post', endpoint, data, options) as Promise<T extends object ? T : any>)
}

async function putRequest<T> (endpoint: string, data?: unknown, options = {}): Promise<T extends object ? T : any> {
  return await (request('put', endpoint, data, options) as Promise<T extends object ? T : any>)
}

async function deleteRequest<T> (endpoint: string, data?: unknown, options = {}): Promise<T extends object ? T : any> {
  return await (request('delete', endpoint, data, options) as Promise<T extends object ? T : any>)
}

export default {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  BASE_URL,
  request
}
